import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { errorMessage } from '../api';
import { SYSTEM_USER_ID, useApproval, useApprovalMutation, useApprovals, type Approval } from '../approvals-queries';
import { useMembers } from '../comms-queries';
import { Alert, Empty, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useSyndicate } from '../session';

export function ApprovalsPage() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const approvals = useApprovals('all');
  return (
    <>
      <PageHeader title={t.approvals.title} />
      {approvals.isPending && <Loading label={t.loading} />}
      {approvals.data?.length === 0 && <Empty>{t.approvals.none}</Empty>}
      <ul className="list-cards">
        {approvals.data?.map((a) => (
          <li key={a.id} className="panel">
            <Link to={a.kind === 'governance.notice' ? `${s.base}/governance/${(a.payload as unknown as { meetingId: string }).meetingId}` : `${s.base}/approvals/${a.id}`}>{a.title}</Link>
            <div className="muted small">
              <span className={`badge badge-approval-${a.status}`}>{t.approvals.status[a.status]}</span> {date(a.createdAt.slice(0, 10))}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export function ApprovalPage() {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const { approvalId = '' } = useParams();
  const approval = useApproval(approvalId);
  const members = useMembers();
  const [excluded, setExcluded] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const decide = useApprovalMutation<{ outcome: 'approved' | 'rejected'; note?: string; excludedItemIds?: string[] }, Approval>((api, base, input) =>
    api(`${base}/approvals/${approvalId}/decide`, { method: 'POST', body: input }),
  );

  if (approval.isPending) return <Loading label={t.loading} />;
  if (approval.isError) return <Alert>{errorMessage(approval.error, t)}</Alert>;
  const a = approval.data;
  const p = a.payload;
  const pending = a.status === 'pending';
  const requester = a.requestedBy === SYSTEM_USER_ID ? t.approvals.system : (members.data?.find((m) => m.userId === a.requestedBy)?.name || '—');
  const included = p.items.filter((i) => !excluded.includes(i.id));
  const toggle = (id: string) => setExcluded((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const result = a.decision?.result;

  return (
    <>
      <Link to={s.base} className="back">
        ← {t.nav.home}
      </Link>
      <PageHeader
        title={a.title}
        intro={`${t.approvals.requestedBy} ${requester} ${t.approvals.on} ${date(a.createdAt.slice(0, 10))}`}
        actions={<span className={`badge badge-approval-${a.status}`}>{t.approvals.status[a.status]}</span>}
      />

      {pending && p.unreconciledDeposits > 0 && (
        <div className="alert alert-error">
          {t.approvals.unreconciled(p.unreconciledDeposits)} <Link to={`${s.base}/finances/bank`}>{t.approvals.reconcile}</Link>
        </div>
      )}
      {result && <Alert tone="info">{t.approvals.result(result.sent, result.skippedPaid.length)}</Alert>}
      {a.decision?.note && <Alert tone="info">{a.decision.note}</Alert>}

      {p.items.length > 0 && (
        <section>
          <h2>{t.approvals.emails}</h2>
          <ul className="list-cards">
            {p.items.map((item) => {
              const isExcluded = excluded.includes(item.id) || a.decision?.excludedItemIds?.includes(item.id);
              return (
                <li key={item.id} className={`panel approval-item${isExcluded ? ' approval-excluded' : ''}`}>
                  <div className="approval-item-head">
                    {pending && a.canDecide && (
                      <label className="inline-field">
                        <input type="checkbox" checked={!excluded.includes(item.id)} onChange={() => toggle(item.id)} />
                        {t.approvals.include}
                      </label>
                    )}
                    <strong>{item.name}</strong>
                    <span className="muted small">{item.email}</span>
                  </div>
                  <div className="small">
                    {t.approvals.stage[item.stage]} · {item.units.map((u) => `${t.requests.unit} ${u.label} (${money(u.balanceCents)})`).join(', ')}
                  </div>
                  <button type="button" className="link-button small" onClick={() => setOpen(open === item.id ? null : item.id)}>
                    {t.approvals.preview}
                  </button>
                  {open === item.id && (
                    <div className="email-preview">
                      <strong>{item.subject}</strong>
                      <pre className="email-body">{item.text}</pre>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {p.escalations.length > 0 && (
        <section>
          <h2>{t.approvals.escalations}</h2>
          <p className="muted">{t.approvals.escalationHelp}</p>
          <ul className="compact-list">
            {p.escalations.map((e) => (
              <li key={e.unitId}>
                <Link to={`${s.base}/units/${e.unitId}`}>{e.label}</Link>
                <span>{e.owners}</span>
                <span className="num">{money(e.balanceCents)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {p.unreachable.length > 0 && (
        <section>
          <h2>{t.approvals.unreachable}</h2>
          <ul className="compact-list">
            {p.unreachable.map((u) => (
              <li key={u.unitId}>
                <Link to={`${s.base}/units/${u.unitId}`}>{u.label}</Link>
                <span>{u.owners}</span>
                <span />
              </li>
            ))}
          </ul>
        </section>
      )}

      {pending &&
        (a.canDecide ? (
          <div className="form-actions sticky-actions">
            <button
              type="button"
              className="button button-primary"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ outcome: 'approved', excludedItemIds: excluded })}
            >
              {p.items.length > 0 ? t.approvals.approve(included.length) : t.approvals.approveOnly}
            </button>
            <button
              type="button"
              className="button"
              disabled={decide.isPending}
              onClick={() => {
                const note = window.prompt(t.approvals.rejectReason);
                if (note !== null) decide.mutate({ outcome: 'rejected', ...(note.trim() ? { note: note.trim() } : {}) });
              }}
            >
              {t.approvals.reject}
            </button>
          </div>
        ) : (
          <Alert tone="info">{t.approvals.canOnlyView}</Alert>
        ))}
      {decide.isError && <Alert>{errorMessage(decide.error, t)}</Alert>}
    </>
  );
}
